import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Paper,
  Grid,
  Pagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  IconButton,
  ListItem,
  Button,
  OutlinedInput,
  InputAdornment,
  CircularProgress,
  DialogActions,
  Alert,
  Stack,
} from '@mui/material';
import { IPurchaseOrder, IPurchaser } from '../../store/interfaces/IPurchaseOrder';
import { PAYMENT_METHODS_ICONS } from '../../constants/icons';
import { PURCHASE_ORDER_STATUS } from '../../constants/purchaseOrderStatus';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Close, DeleteForever, StarBorder, Visibility, VisibilityOff } from '@mui/icons-material';
import { useIsMobileView } from '../../utils/responsive';
import { useAppSelector } from '../../store/hooks/useAppSelector';
import { RootState } from '../../store/types/rootState';
import { useCancelTicketsMutation, useGenerateTicketsMutation, useGetBlockedNumbersByRaffleIdQuery, useLazyGetTicketsByPurchaserEmailQuery, usePaymentCommitTransactionMutation, usePaymentInitTransactionMutation, useRejectPurchaseOrderMutation } from '../../store/api';
import { enqueueSnackbar } from 'notistack';
import { GeneratedTickets } from '../raffle/tickets/generatedTickets';
import { getIconFromBankId } from '../../constants/banks';
import { BANK_ACCOUNTS, BankAccountItem } from '../../constants/bankAccounts';
import { IRaffle } from '../../store/interfaces/IRaffle';
import { Delete, DeleteIcon, Eraser, Plus } from 'lucide-react';
import { ITopPurchaser } from '../../store/interfaces/ITopPurchaser';
import { getVoucherImageUrl, VoucherPreview, VoucherStatusChip } from '../common/VoucherPreview';

interface IPurchaseOrderProps {
  purchaseOrders: IPurchaseOrder[];
  selectedRaffle: IRaffle;
  refetch: () => void;
}

export const PurchaseOrderTable = ({ purchaseOrders, selectedRaffle, refetch }: IPurchaseOrderProps) => {
  const [statusFilter, setStatusFilter] = useState('');
  const [targetAccountFilter, setTargetAccountFilter] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [ticketNumbersFilter, setTicketNumbersFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const isMobile = useIsMobileView();
  const [openImageModal, setOpenImageModal] = React.useState(false);
  const [openRevertPurchaseModal, setOpenRevertPurchaseModal] = React.useState(false);
  const [openConfirmPurchaseOrderRejectModal, setOpenConfirmPurchaseOrderRejectModal] = React.useState(false);
  const [openSingleImageModal, setOpenSingleImageModal] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [selectedTickets, setSelectedTickets] = React.useState<number[] | undefined>();
  const loggedInAccountId = useAppSelector(
    ({ account }: RootState) => account.id,
  );
  const loggedUserEmail = useAppSelector(
    ({ user }: RootState) => user.email,
  );

  const [findTicketsByEmail] = useLazyGetTicketsByPurchaserEmailQuery();

  const { data: currentBlockedNumbers } = useGetBlockedNumbersByRaffleIdQuery(selectedRaffle.id, {
    refetchOnFocus: false,
    skip: loggedUserEmail !== 'oscaralarcon@gmail.com'
  });

  const [purchaserTicketCount, setPurchaserTicketCount] = useState<Number>(0);
  const handleValidateStatus = (order: IPurchaseOrder) => {
    if (
      order.paymentMethod === 'transfer' &&
      order.status === 'TRANSFER_PAYMENT_CREATED'
    ) {
      setOrderId(order.id)
      findTicketsByEmail({
        email: order.purchaser.email,
        raffleId: selectedRaffle.id,
      }).then(response => {
        setSelectedGiftNumber(undefined)
        if(response.data) {
          setPurchaserTicketCount(response.data.length)
          setGeneratedNumbers(response.data)
        } else {
          setPurchaserTicketCount(0)
          setGeneratedNumbers([])
        }
      }).catch(error => (
        setPurchaserTicketCount(0)
      ))
      setSelectedImage(getVoucherImageUrl(loggedInAccountId, selectedRaffle.id, order, selectedRaffle.type));
      setSelectedTickets(order.selectedTickets);
      setOpenImageModal(true);
    }
  };

  const [selectedGiftNumber, setSelectedGiftNumber] = useState<Number | undefined>();

  const handleCloseImageModal = () => {
    setSelectedImage(null);
    setSelectedTickets(undefined);
    setOpenImageModal(false);
  };
  
  const handleCloseRevertPurchaseModal = () => {
    setSelectedImage(null);
    setSelectedTickets(undefined);
    setOpenRevertPurchaseModal(false);
    setPurchaserToBeTicketsCancel(undefined);
    setCancelTicketsEmailConfirmation(undefined);
  };

  const handleCloseSingleImageModal = () => {
    setSelectedImage(null);
    setSelectedTickets(undefined);
    setOpenSingleImageModal(false);
  };

  const [
    paymentCommit, { data, isLoading: isLoadingCommitTransaction },
  ] = usePaymentCommitTransactionMutation();

  const [
    rejectPurchaseOrder, { data: rejectPurchaseOrderData, isLoading: isLoadingPurchaseOrderRejection },
  ] = useRejectPurchaseOrderMutation();
  
  const [
    cancelTickets, { data: cancelTicketsData, isLoading: isCancelingTickets },
  ] = useCancelTicketsMutation();



  const [
    generateTickets,
    {
      data: generateTicketsResponse,
      isLoading: isLoadingGenerateTickets,
    },
  ] = useGenerateTicketsMutation();


  useEffect(() => {
    if (rejectPurchaseOrderData) {
      enqueueSnackbar("Orden de compra rechazada!", {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
      setOpenConfirmPurchaseOrderRejectModal(false)
      setOpenSingleImageModal(false)
      setOpenImageModal(false)
      refetch();
    }
  }, [rejectPurchaseOrderData]);

  useEffect(() => {
    if (cancelTicketsData) {
      enqueueSnackbar("Tickets cancelados con éxito!", {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
      setOpenRevertPurchaseModal(false)
      setSelectedImage(null);
      setSelectedTickets(undefined);
      setOpenRevertPurchaseModal(false);
      setPurchaserToBeTicketsCancel(undefined);
      setCancelTicketsEmailConfirmation(undefined);
      refetch();
    }
  }, [cancelTicketsData]);


  useEffect(() => {
    if (data?.purchaseOrder && data.status !== "FAILED") {
      generateTickets({
        config: {
          purchaseOrderId: data.purchaseOrder,
          accountId: loggedInAccountId,
          number: selectedGiftNumber,
        },
        raffleId: selectedRaffle.id,
      });
    } else if (data?.status === "FAILED") {
      enqueueSnackbar("Error al momento validar el pago!", {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    }
  }, [data, generateTickets, loggedInAccountId, selectedRaffle.id]);

  useEffect(() => {
    if (generateTicketsResponse && generateTicketsResponse.status === "SUCCESSFUL") {
      enqueueSnackbar("Tickets generados exitosamente!", {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
      refetch();
      setOpenInitModal(false);
      setOpenImageModal(false);
    } else if (generateTicketsResponse && generateTicketsResponse.status === "PROCESS_INITIALIZED") {
      enqueueSnackbar(`Se están generando los tickets, enviaremos el correo al usuario con sus números.`, {
        variant: "info",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    }
  }, [setOpenImageModal, generateTicketsResponse])



  const [orderId, setOrderId] = useState<string>("")
  const [purchaserToBeTicketsCancel, setPurchaserToBeTicketsCancel] = useState<IPurchaseOrder | undefined>()

  const handleCommitTransaction = () => {
    paymentCommit({
      conf: {
        paymentMethodId: 'transfer',
        orderId,
        password
      },
      raffleId: selectedRaffle.id,
    });
  }

  const handlePurchaseOrderReject = () => {
    rejectPurchaseOrder({
      raffleId: selectedRaffle.id,
      purchaseOrderId: orderId
    });
  }

  const handleCancelTickets = () => {
    cancelTickets({
      raffleId: selectedRaffle.id,
      purchaseOrderId: orderId,
      passwordRequest: password,
      raffleName: selectedRaffle.name,
    });
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const [password, setPassword] = React.useState("");
  const [cancelTicketsEmailConfirmation, setCancelTicketsEmailConfirmation] = React.useState<string | undefined>();
  const [showPassword, setShowPassword] = React.useState(false);
  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };


  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      const jsDate = new Date(date);
      jsDate.setHours(0, 0, 0, 0);
      setStartDate(jsDate);
    } else {
      setStartDate(null);
    }
    setPage(0);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDate(date);
    } else {
      setEndDate(null);
    }
    setPage(0);
  };


  const handleStatusChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleTargetAccountFilter = (event: any) => {
    setTargetAccountFilter(event.target.value);
    setPage(0);
  };

  const handlePaymentMethodChange = (event: any) => {
    setPaymentMethodFilter(event.target.value);
    setPage(0);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailFilter(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleTicketNumbersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTicketNumbersFilter(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameFilter(event.target.value.toLowerCase());
    setPage(0);
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneFilter(event.target.value.toLowerCase());
    setPage(0);
  };

  const filteredData = purchaseOrders
    .filter((order: IPurchaseOrder) => {
      if (statusFilter && order.status !== statusFilter) {
        return false;
      }
      if (targetAccountFilter && order.targetAccountKey !== targetAccountFilter) {
        return false;
      }
      if (paymentMethodFilter && order.paymentMethod !== paymentMethodFilter) {
        return false;
      }
      if (emailFilter && !order.purchaser.email.toLowerCase().includes(emailFilter)) {
        return false;
      }
      if (nameFilter && !order.purchaser.name.toLowerCase().includes(nameFilter)) {
        return false;
      }
      if (phoneFilter && !order.purchaser.phone.toLowerCase().includes(phoneFilter)) {
        return false;
      }
      if (ticketNumbersFilter && `${order.ticketQuantity}` != ticketNumbersFilter) {
        return false;
      }
      if (startDate && dayjs(order.createdDate) < dayjs(startDate).hour(0).minute(0).second(0)) {
        return false;
      }
      if (endDate && dayjs(order.createdDate) > dayjs(endDate).hour(23).minute(59).second(59)) {
        return false;
      }
      return true;
    })
    .sort((a: IPurchaseOrder, b: IPurchaseOrder) => {
      return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
    });

  const [openViewNumbersModal, setOpenViewNumbersModal] = React.useState(false);
  const [generatedNumbers, setGeneratedNumbers] = useState<number[]>([]);

  const handleViewGeneratedNumbers = (order: IPurchaseOrder) => {
    if (
      (order.paymentMethod === 'transfer' || order.paymentMethod === 'cash') &&
      (order.status === 'PROCESS_FINISHED' || order.status === 'SENDING_MAIL_ERROR' || order.status === 'PURCHASE_ORDER_CANCELED' )
    ) {
      setOrderId(order.id)
      if (order.selectedTickets) {
        setGeneratedNumbers(order.selectedTickets)
      }
      setOpenViewNumbersModal(true);
    }
  };

  const formateador = new Intl.NumberFormat('es-CL')

  function formatCurrency(order: IPurchaseOrder): string {
    if (!order?.targetAccount?.id) return "";

    const foundedBankAccount = bankAccounts.find(
      bankAccount => bankAccount.bankId === order.targetAccount!.id
    );


    if (!foundedBankAccount) {
      if (order.paymentMethod == 'cash') {
        return "Efectivo"
      }
      return "";
    };

    const totalAmount = order.ticketQuantity * foundedBankAccount.ticketPriceAmount;
    const formattedAmount = formateador.format(totalAmount);
    const currency = foundedBankAccount.ticketCurrency ?? "";

    return `${formattedAmount} ${currency}`;
  }

  const pageCount = Math.ceil(filteredData.length / rowsPerPage);

  const currentPageData = filteredData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickViewSomething = (order: IPurchaseOrder) => {
    if (
      order.paymentMethod === 'transfer'
    ) {
      setSelectedImage(getVoucherImageUrl(loggedInAccountId, selectedRaffle.id, order, selectedRaffle.type));
      setOpenSingleImageModal(true);
    }
  }

  const [bankAccounts, setBankAccounts] = useState<BankAccountItem[]>([]);

  const [openInitModal, setOpenInitModal] = useState(false);
  const [purchaserData, setPurchaserData] = useState({ name: '', email: '', phone: '' });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [ticketQuantity, setTicketQuantity] = useState(1);

   const [initTransaction, { isLoading: isLoadingInitTransaction, data: dataInitTransaction, error: isErrorInitTransaction }] = usePaymentInitTransactionMutation();
  

  const handleInitTransaction = async () => {
    try {
      if (!purchaserData.name || !purchaserData.email || !purchaserData.phone) {
        enqueueSnackbar("Ingrese todos los datos de contacto.", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
        return;
      }

      if (!selectedPaymentMethod) {
        return;
      }

      const response = await initTransaction({
        raffleId: selectedRaffle.id,
        conf: {
          ticketQuantity,
          passwordCash: password,
          paymentMethodId: selectedPaymentMethod,
          purchaser: purchaserData,
          targetBankAccount: {
            accountNumber: 'cash',
            id: 'cash',
            name: 'cash'
          }
        },
      }).unwrap();

      if (
        response.status === "AUTHORIZED_FOR_GENERATE_TICKETS" &&
        selectedPaymentMethod === "cash"
      ) {

        console.log("response ")
        const commitResponse = await paymentCommit({
          conf: {
            paymentMethodId: "cash",
            orderId: response.purchaseOrderId,
          },
          raffleId: selectedRaffle.id,
        });

        return;
      }


      if (response.status === "LIMIT_EXCEEDED" || response.status === "RAFFLE_NO_LONGER_AVAILABLE" || response.status === "UNAUTHORIZED_FOR_GENERATE_TICKETS") {
        enqueueSnackbar(response.error, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      } else {
        enqueueSnackbar("Error al momento de ir al pago!", {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
        });
      }
    } catch (error) {
      enqueueSnackbar("Error al momento de ir al pago!", {
        variant: "error",
        anchorOrigin: {
          horizontal: "right",
          vertical: "top",
        },
      });
    }
  };

  useEffect(() => {
    if (selectedRaffle && selectedRaffle.bankAccounts) {
      setBankAccounts(selectedRaffle.bankAccounts)
    } else {
      const config = BANK_ACCOUNTS.find(bankAccount => bankAccount.accountId === loggedInAccountId);
      if (config != undefined) {
        setBankAccounts(config.bankAccounts)
      }
    }
  }, [selectedRaffle]);



  if (!bankAccounts) {
    return <></>
  }



  return (
    <Box mt={2}>
      <Dialog open={openInitModal} onClose={() => setOpenInitModal(false)} fullWidth maxWidth="sm">
  <DialogTitle>Crear Orden de Compra</DialogTitle>
  <DialogContent>
    <Box display="flex" flexDirection="column" gap={2} mt={1} width={"100%"}>
      <TextField
          label="Nombre"
          fullWidth
          value={purchaserData.name}
          onChange={(e) => setPurchaserData({ ...purchaserData, name: e.target.value })}
        />
        <TextField
          label="Email"
          fullWidth
          value={purchaserData.email}
          onChange={(e) => setPurchaserData({ ...purchaserData, email: e.target.value })}
        />
        <TextField
          label="Teléfono"
          fullWidth
          value={purchaserData.phone}
          onChange={(e) => setPurchaserData({ ...purchaserData, phone: e.target.value })}
        />
        <TextField
          label="Cantidad de Tickets"
          type="number"
          fullWidth
          value={ticketQuantity}
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value <= 100) {
              setTicketQuantity(value);
            }
          }}
          inputProps={{ min: 1, max: 100 }}
        />
        <FormControl fullWidth>
          <InputLabel>Metodo de Pago</InputLabel>
          <Select
            disabled
            value={selectedPaymentMethod}
            onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            label="Metodo de Pago"
          >
            <MenuItem value="cash">Efectivo</MenuItem>
            {/* You can add more methods if needed */}
          </Select>
        </FormControl>
        <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="password">Contraseña</InputLabel>
          <OutlinedInput
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handleChangePassword}
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Contraseña"
          />
        </FormControl>
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setOpenInitModal(false)}>Cancelar</Button>
      {!isLoadingCommitTransaction && !isLoadingGenerateTickets && <Button onClick={handleInitTransaction} variant="contained" color="primary">
        Crear Orden
      </Button>}
      {isLoadingCommitTransaction && (<LoadingComponent message="Confirmando orden de compra" />)}
        {isLoadingGenerateTickets && (<LoadingComponent message="Generando tickets" />)}
    </DialogActions>
  </Dialog>
      <Dialog
        open={openViewNumbersModal}
        onClose={() => setOpenViewNumbersModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <IconButton
          aria-label="close"
          onClick={() => setOpenViewNumbersModal(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <GeneratedTickets generatedTickets={generatedNumbers} limit={selectedRaffle.limit}></GeneratedTickets>
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <Box paddingY={2} display={"flex"} justifyContent={"end"}>
        <Button endIcon={<Plus></Plus>} onClick={() => setOpenInitModal(true)}>
          Crear Orden De Compra
        </Button>
      </Box>
      <Card variant="outlined" sx={{ pb: 10 }}>
        <CardContent>
          {purchaseOrders.length === 0 ? (
            <Typography variant="body1">No hay ordenes de compra</Typography>
          ) : (
            <>
              <Box paddingBottom={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Estado</InputLabel>
                      <Select
                        label="Estado"
                        value={statusFilter}
                        onChange={handleStatusChange}
                        fullWidth
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {Array.from(
                          new Set(
                            purchaseOrders.map(
                              (order: IPurchaseOrder) => order.status
                            )
                          )
                        ).map((status) => (
                          <MenuItem key={status} value={status}>
                            {PURCHASE_ORDER_STATUS.find(pos => pos.id === status)?.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {!isMobile && <>

                    <Grid item xs={12} sm={12} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>Método de pago</InputLabel>
                        <Select
                          label="Método de pago"
                          value={paymentMethodFilter}
                          onChange={handlePaymentMethodChange}
                          fullWidth
                        >
                          <MenuItem value="">Todos</MenuItem>
                          {Array.from(
                            new Set(
                              purchaseOrders.map(
                                (order: IPurchaseOrder) => order.paymentMethod
                              )
                            )
                          ).map((paymentMethod) => (
                            <MenuItem key={paymentMethod} value={paymentMethod}>
                              {PAYMENT_METHODS_ICONS("24px").find(paymentMethodE => paymentMethodE.id === paymentMethod)?.icon}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>}
                  <Grid item xs={12} sm={12} md={3}>
                    <TextField
                      label="Filtrar por email"
                      variant="outlined"
                      value={emailFilter}
                      onChange={handleEmailChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3}>
                    <TextField
                      label="Filtrar por nombre"
                      variant="outlined"
                      value={nameFilter}
                      onChange={handleNameChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={2}>
                    <TextField
                      label="Filtrar por teléfono"
                      variant="outlined"
                      value={phoneFilter}
                      onChange={handlePhoneChange}
                      fullWidth
                    />
                  </Grid>
                  {!isMobile && <>
                    <Grid item xs={12} sm={12} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>Cuenta de destino</InputLabel>
                        <Select
                          label="Cuenta de destino"
                          value={targetAccountFilter}
                          onChange={($event) => {
                            console.log($event)
                            handleTargetAccountFilter($event)
                          }}
                          fullWidth
                        >
                          <MenuItem value="">
                            <StarBorder></StarBorder>
                            <Typography sx={{ paddingLeft: 1 }}>Todas</Typography>
                          </MenuItem>
                          {bankAccounts && bankAccounts.map((bankAccount) => (
                            <MenuItem key={bankAccount.accountNumber} value={bankAccount.bankId + (bankAccount.accountNumber || '')}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                {getIconFromBankId({
                                  bankId: bankAccount.bankId || "",
                                  height: 20,
                                  width: 20
                                })}
                                <Typography sx={{ paddingLeft: 1, display: 'inline' }}>{bankAccount.name}</Typography>
                              </div>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                  }

                  <Grid item xs={12} sm={12} md={1}>
                    <TextField
                      label="# tickets"
                      variant="outlined"
                      id='ticketsnumber'
                      autoComplete='off'
                      value={ticketNumbersFilter}
                      onChange={handleTicketNumbersChange}
                      fullWidth
                    />
                  </Grid>
                  {!isMobile && <>
                    <Grid item xs={12} sm={12} md={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          sx={{
                            width: "100%"
                          }}
                          label="Fecha de Creación"
                          value={startDate}
                          onChange={(date: Date | null) => {
                            handleStartDateChange(date);
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12} md={2}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          sx={{
                            width: "100%"
                          }}
                          label="Hasta"
                          value={endDate}
                          onChange={(date: Date | null) => {
                            handleEndDateChange(date);
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </>}
                  <Grid item xs={12} sm={12} md={1}>
                    <Box height={"100%"} width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                      <Button onClick={refetch} variant='contained' size='small'>Recargar</Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={12} md={1}>
                    <Box height={"100%"} width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                      <InfoOutlinedIcon onClick={handleClickOpen} fontSize='large' sx={{
                        cursor: "pointer"
                      }} />
                    </Box>
                    <Dialog
                      open={open}
                      onClose={handleClose}
                      aria-labelledby="alert-dialog-title"
                      aria-describedby="alert-dialog-description"
                    >
                      <IconButton
                        aria-label="close"
                        onClick={() => setOpen(false)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                          color: (theme) => theme.palette.grey[500],
                        }}
                      >
                        <Close />
                      </IconButton>
                      <DialogTitle id="alert-dialog-title">
                        {"Estados de pago"}
                      </DialogTitle>
                      <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                          {PURCHASE_ORDER_STATUS.map(({ id, title, description }) => (
                            <ListItem key={id}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={4} lg={4} key={id}>
                                  <Chip
                                    label={title}
                                    color={getStatusColor(id)}
                                    variant="outlined"
                                    sx={{
                                      width: '100%',
                                      marginRight: 2,
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={12} md={8} lg={8} key={id}>
                                  <Typography>{description}</Typography>
                                </Grid>
                              </Grid>
                            </ListItem>
                          ))}
                        </DialogContentText>
                      </DialogContent>
                    </Dialog>
                    <Dialog
                      open={openConfirmPurchaseOrderRejectModal}
                      onClose={() => setOpenConfirmPurchaseOrderRejectModal(false)}
                      aria-labelledby="alert-dialog-title"
                      aria-describedby="alert-dialog-description"
                    >
                      <IconButton
                        aria-label="close"
                        onClick={() => setOpenConfirmPurchaseOrderRejectModal(false)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                          color: (theme) => theme.palette.grey[500],
                        }}
                      >
                        <Close />
                      </IconButton>
                      <DialogTitle id="alert-dialog-title">
                        {"Confirmar rechazo de la orden de compra"}
                      </DialogTitle>
                      <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                          Seguro que quiere rechazar la orden de compra? Esta acción no se puede deshacer
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <Button variant='contained' onClick={handlePurchaseOrderReject}>Confirmar</Button>
                      </DialogActions>
                    </Dialog>
                  </Grid>
                </Grid>
              </Box>
              <Paper sx={{ width: "100%", overflow: "hidden" }}>
                {!isMobile ?
                  <TableContainer sx={{ maxHeight: "60vh" }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            fecha
                          </TableCell>
                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            # tickets
                          </TableCell>
                          {/* <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            monto
                          </TableCell> */}

                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            método de pago
                          </TableCell>

                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            nombre
                          </TableCell>

                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            userID
                          </TableCell>

                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            teléfono
                          </TableCell>
                          <TableCell
                            style={{
                              backgroundColor: "black",
                              color: "white",
                              whiteSpace: "nowrap",
                            }}
                          >
                            estado
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentPageData.map(
                          (order: IPurchaseOrder, index: number) => (
                            <TableRow key={index}>
                              <TableCell sx={{
                                whiteSpace: "nowrap",
                              }}>
                                {dayjs(order.createdDate).format(
                                  "YYYY-MM-DD HH:mm:ss"
                                )}
                              </TableCell>
                              <TableCell>
                                <Box>
                                  <Chip
                                    label={`${order.ticketQuantity} - ${formatCurrency(order)}`}
                                    color={getStatusColor(order.status)}
                                    onClick={() => handleViewGeneratedNumbers(order)}
                                    variant="outlined"
                                    sx={{
                                      width: '100%',
                                    }} />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Button onClick={() => handleClickViewSomething(order)}>
                                  {PAYMENT_METHODS_ICONS("25px").find(
                                    (icon) => icon.id === order.paymentMethod
                                  )?.icon}

                                  {order.targetAccount?.name && <>
                                    {getIconFromBankId({
                                      bankId: order.targetAccount?.id || "",
                                      height: 25,
                                      width: 25
                                    })}
                                    <Typography variant='body2' style={{ paddingLeft: 2, textTransform: 'capitalize' }}>({order.targetAccount?.name})</Typography>
                                  </>}

                                </Button>
                              </TableCell>
                              <TableCell>{order.purchaser.name}</TableCell>
                              <TableCell>{order.purchaser.email}</TableCell>
                              <TableCell>{order.purchaser.phone}</TableCell>
                              <TableCell>
                                <Grid container>
                                  <Grid item pl={2} width={'100%'}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        { (order.status === 'PROCESS_FINISHED' || order.status === 'SENDING_MAIL_ERROR') && 
                                          <Button size='small' color='error' variant='outlined' onClick={() => {
                                              setOrderId(order.id)
                                              setSelectedTickets(order.selectedTickets);
                                              setOpenRevertPurchaseModal(true);
                                              setPurchaserToBeTicketsCancel(order)
                                          }}>
                                            <Eraser />
                                          </Button> 
                                        }
                                        <Chip
                                          label={PURCHASE_ORDER_STATUS.find(pos => pos.id === order.status)?.title}
                                          color={getStatusColor(order.status)}
                                          onClick={() => handleValidateStatus(order)}
                                          variant="outlined"
                                          sx={{
                                            width: '100%',
                                          }}
                                        />
                                      </Stack>
                                      <VoucherStatusChip order={order} raffleType={selectedRaffle.type} sx={{ mt: 1, width: '100%' }} />
                                  </Grid>
                                </Grid>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  : <>
                    <Grid container spacing={4}>
                      {currentPageData.map((order: IPurchaseOrder, index: number) => (
                        <Grid item xs={12} sm={6} md={4} key={index} my={2}>
                          <Card sx={{ p: 2 }}>
                            <CardContent>
                              {/* Date */}
                              <Typography variant="caption" color="text.secondary">
                                {dayjs(order.createdDate).format("YYYY-MM-DD HH:mm:ss")}
                              </Typography>

                              {/* Ticket & Amount */}
                              <Box mt={1}>
                                <Alert
                                  color={getStatusColor(order.status)}
                                  onClick={() => handleViewGeneratedNumbers(order)}
                                  variant="outlined"
                                  severity='info'
                                  sx={{ width: "100%" }}
                                >
                                  {`${order.ticketQuantity} - ${formatCurrency(order)}`}
                                </Alert>
                              </Box>

                              {/* Payment Method & Bank Icon */}
                              <Box mt={2}>
                                <Button fullWidth onClick={() => handleClickViewSomething(order)}>
                                  {PAYMENT_METHODS_ICONS("25px").find(
                                    (icon) => icon.id === order.paymentMethod
                                  )?.icon}

                                  {order.targetAccount?.name && (
                                    <>
                                      {getIconFromBankId({
                                        bankId: order.targetAccount?.id || "",
                                        height: 25,
                                        width: 25,
                                      })}
                                      <Typography
                                        variant="body2"
                                        sx={{ paddingLeft: 1, textTransform: "capitalize" }}
                                      >
                                        ({order.targetAccount?.name})
                                      </Typography>
                                    </>
                                  )}
                                </Button>
                              </Box>

                              {/* Purchaser Info */}
                              <Box mt={2}>
                                <Typography variant="body1">{order.purchaser.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {order.purchaser.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {order.purchaser.phone}
                                </Typography>
                              </Box>

                              {/* Order Status */}
                              <Box mt={2} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                                { order.status === 'PROCESS_FINISHED' && 
                                  <Button size='small' color='error' variant='outlined' onClick={() => {
                                      setOrderId(order.id)
                                      setSelectedTickets(order.selectedTickets);
                                      setOpenRevertPurchaseModal(true);
                                      setPurchaserToBeTicketsCancel(order)
                                  }}>
                                    <Eraser />
                                  </Button> 
                                }
                                <Chip
                                  label={
                                    PURCHASE_ORDER_STATUS.find(
                                      (pos) => pos.id === order.status
                                    )?.title
                                  }
                                  color={getStatusColor(order.status)}
                                  onClick={() => handleValidateStatus(order)}
                                  variant="outlined"
                                  sx={{ width: "100%", ml: 1 }}
                                />
                              </Box>
                              <VoucherStatusChip order={order} raffleType={selectedRaffle.type} sx={{ mt: 1, width: '100%' }} />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>

                  </>}
                <Box my={2} display={"flex"} justifyContent={"center"}>
                  <Pagination
                    count={pageCount}
                    page={page + 1}
                    onChange={(event, value) => setPage(value - 1)}
                    color="primary"
                  />
                </Box>
              </Paper>
            </>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={openSingleImageModal}
        onClose={handleCloseSingleImageModal}
        aria-labelledby="image-modal-title"
        maxWidth="md"
        fullScreen={isMobile}
      >
        <IconButton
          aria-label="close"
          onClick={handleCloseSingleImageModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container>
            <Grid
              item
              sx={{
                width: "100%",
              }}
            >
              {selectedImage && (
                  <VoucherPreview
                    src={selectedImage}
                    style={{ width: '100%' }}
                  />
              )}
            </Grid>
            <Grid
              item
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <Button onClick={handleCloseSingleImageModal} variant='outlined' sx={{ marginBottom: 2 }}>
                Cerrar
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-labelledby="image-modal-title"
        maxWidth="md"
        fullScreen={isMobile}
      >
        <IconButton
          aria-label="close"
          onClick={() => setOpenImageModal(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>Validar comprobante</DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid
              item
              sx={{
                width: "100%",
              }}
            >
              {selectedImage && (
                  <VoucherPreview
                    src={selectedImage}
                    style={{ height: '500px' }}
                  />
              )}
            </Grid>
            <Grid
              item
              sx={{
                width: "100%",
              }}
            >
              {selectedTickets && selectedTickets.length > 0 && <Box m={2}>
                <Typography fontWeight={"bold"} mb={2}>
                  Tickets seleccionados
                </Typography>
                <Box display={"flex"} flexWrap={"wrap"} justifyContent={"center"} maxHeight={"100px"} overflow={"auto"}>
                  {selectedTickets?.map(t => (
                      <Typography fontWeight={"bold"} sx={{ m: 1 }}> {t} </Typography>
                  ))}
                </Box>
              </Box>}


              {loggedUserEmail === 'oscaralarcon@gmail.com' && (
                  <Grid container>

                    <Grid xs={6} xl={6} lg={6} md={6} sm={6}>
                      <Box mt={2} p={2} borderRadius={2} boxShadow={2} m={1}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Números comprados
                        </Typography>
                        <Button onClick={() => setOpenViewNumbersModal(true)}>
                          <Chip 
                            label={`${purchaserTicketCount} números`} 
                            color="primary" 
                            variant="outlined"
                            sx={{ fontSize: '1rem', padding: '0 10px' }}
                          />
                        </Button>
                      </Box>
                    </Grid>
                    <Grid xs={6} xl={6} lg={6} md={6} sm={6}>
                    <Box mt={2} p={2} borderRadius={2} boxShadow={2} m={1}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Selección:
                      </Typography>
                      
                      <FormControl fullWidth size="small">
                        <Select
                          value={selectedGiftNumber}
                          onChange={(e) => setSelectedGiftNumber(Number(e.target.value))}
                        >
                          {currentBlockedNumbers && currentBlockedNumbers.map((item: any, index: number) => (
                            <MenuItem key={index} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    </Grid>

                  </Grid>
                )}

              

              {!isLoadingCommitTransaction && !isLoadingGenerateTickets && <Button
                fullWidth
                type="submit"
                color="primary"
                disabled={
                  password === ""
                }
                sx={{
                  opacity: password === "" ? 0.3 : 1,
                  backgroundColor: "#FFF !important",
                  mt: 2,
                  mb: 5
                }}
                variant="contained"

                onClick={() => setOpenConfirmPurchaseOrderRejectModal(true)}
              >
                <DeleteForever sx={{ fontSize: 25 }}></DeleteForever>
                <Typography color={"#000"} ml={1} fontWeight={"bold"}>
                  Cancelar orden de compra
                </Typography>
              </Button>}

              <Typography>
                Esta acción requiere contraseña.
              </Typography>

              <FormControl sx={{ width: "100%", mt: 2 }} variant="outlined">
                <InputLabel htmlFor="password-generate-tickets">
                  Password
                </InputLabel>
                <OutlinedInput
                  id="password-generate-tickets"
                  type={showPassword ? "text" : "password"}
                  onChange={handleChangePassword}
                  fullWidth
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Password"
                />
              </FormControl>

              {!isLoadingCommitTransaction && !isLoadingGenerateTickets && <Button
                fullWidth
                type="submit"
                color="primary"
                disabled={
                  password === ""
                }
                sx={{
                  opacity: password === "" ? 0.3 : 1,
                  backgroundColor: "#FFF !important",
                  mt: 2,
                }}
                variant="contained"
                startIcon={
                  PAYMENT_METHODS_ICONS("30px").find(
                    (element) => element.id === "transfer"
                  )?.icon
                }
                onClick={handleCommitTransaction}
              >
                <Typography color={"#000"} ml={1} fontWeight={"bold"}>
                  Generar tickets
                </Typography>
              </Button>}
              {isLoadingCommitTransaction && (<LoadingComponent message="Confirmando orden de compra" />)}
              {isLoadingGenerateTickets && (<LoadingComponent message="Generando tickets" />)}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    <Dialog
  open={openRevertPurchaseModal}
  onClose={handleCloseRevertPurchaseModal}
  aria-labelledby="revert-purchase-title"
  maxWidth="md"
  fullScreen={isMobile}
>
  <IconButton
    aria-label="close"
    onClick={() => setOpenRevertPurchaseModal(false)}
    sx={{
      position: 'absolute',
      right: 8,
      top: 8,
      color: (theme) => theme.palette.grey[500],
    }}
  >
    <CloseIcon />
  </IconButton>

  <DialogTitle>
    Cancelar los tickets de {purchaserToBeTicketsCancel?.purchaser?.name}
  </DialogTitle>

  <DialogContent>
    <Grid container spacing={3}>
      
      {/* Imagen seleccionada */}
      {selectedImage && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center">
            <VoucherPreview
              src={selectedImage}
              alt="Comprobante"
              style={{ maxHeight: 400, maxWidth: "100%", borderRadius: 8 }}
            />
          </Box>
        </Grid>
      )}

      {/* Información de la orden */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Grid container>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Typography>
                <strong>Comprador:</strong> {purchaserToBeTicketsCancel?.purchaser?.name}
              </Typography>
              <Typography>
                <strong>Aprobado por:</strong> {purchaserToBeTicketsCancel?.approvedBy || 'admin'} 
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Typography>
                <strong>Email:</strong> {purchaserToBeTicketsCancel?.purchaser?.email}
                <br />
                <strong>Fecha de aprobación:</strong> {dayjs(purchaserToBeTicketsCancel?.updatedDate).format("YYYY-MM-DD HH:mm:ss")}
          </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Advertencia */}
      <Grid item xs={12}>
        <Alert severity="warning" variant="outlined">
          <Typography fontWeight="bold" mb={1}>
            ¿Seguro que quieres cancelar los tickets?
          </Typography>
          <Typography>
            Se enviará un correo al comprador notificando la cancelación. <br />
            Los números asignados se liberarán y podrán ser comprados nuevamente. <br />
            <strong>Esta acción no se puede deshacer.</strong>
          </Typography>
        </Alert>
      </Grid>

      {/* Confirmación */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Confirmación requerida
          </Typography>

          <Typography variant="body1" mb={2}>
            Para confirmar la cancelación, escribe el correo del usuario:{" "}
            <strong>{purchaserToBeTicketsCancel?.purchaser?.email}</strong>
          </Typography>

          <FormControl fullWidth variant="outlined" margin="normal" sx={{ mb: 2 }}>
            <InputLabel htmlFor="confirm-email">Confirmar email</InputLabel>
            <OutlinedInput
              id="confirm-email"
              label="Confirmar email"
              onChange={(event) => setCancelTicketsEmailConfirmation(event.target.value)}
            />
          </FormControl>

            <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="password-generate-tickets">Contraseña</InputLabel>
          <OutlinedInput
            id="password-generate-tickets"
            type={showPassword ? "text" : "password"}
            label="Contraseña"
            onChange={handleChangePassword}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        </Paper>
      </Grid>

      {/* Botón de acción */}
      <Grid item xs={12}>
        {!isCancelingTickets ? (
          <Button
            fullWidth
            variant="contained"
            sx={{ backgroundColor: "red", color: "#fff", py: 1.5 }}
            disabled={password === "" || cancelTicketsEmailConfirmation !== purchaserToBeTicketsCancel?.purchaser?.email}
            onClick={handleCancelTickets}
            startIcon={<DeleteForever />}
          >
            <Typography fontWeight="bold">Cancelar tickets</Typography>
          </Button>
        ) : (
          <LoadingComponent message="Cancelando tickets" />
        )}
      </Grid>
    </Grid>
  </DialogContent>
</Dialog>

    </Box>
  );
};


interface LoadingComponentProps {
  message: string;
}
const LoadingComponent = ({ message }: LoadingComponentProps) => (
  <Box
    m={3}
    display="flex"
    flexDirection="column"
    alignItems="center"
    width="100%"
    justifyContent="center"
  >
    <Typography>{message}</Typography>
    <CircularProgress
      variant="indeterminate"
      sx={{
        color: "#000",
      }}
    />
  </Box>
);

const getStatusColor = (status: string): "success" | "error" | "info" => {
  switch (status) {
    case 'PROCESS_FINISHED':
      return 'success';
    case 'PAYMENT_FAILED':
    case 'PURCHASE_ORDER_REJECTED':
    case 'PURCHASE_ORDER_CANCELED':
    case 'CASH_PAYMENT_BAD_CREDENTIALS':
      return 'error';
    default:
      return 'info';
  }
};
